// File: components/VariablePickerDrawer.jsx
import React, { useState } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  HStack,
  VStack,
  Text,
  Button,
  Icon,
  Center,
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Code,
  Tag
} from '@chakra-ui/react';
import { FaPlug, FaUser, FaCode, FaInfoCircle, FaSearch, FaBolt } from 'react-icons/fa';
import { MdFlashOn } from 'react-icons/md';

// ─── Static Flow Context Variables (always available) ─────────────────────────
const FLOW_CONTEXT_SECTIONS = [
  {
    id: 'flow_execution',
    label: 'Flow Execution',
    icon: MdFlashOn,
    color: 'orange',
    variables: [
      { path: 'flow.execution_id',   description: 'Unique ID for this flow run' },
      { path: 'flow.trigger_time',     description: 'Flow Trigger Time' },
      { path: 'flow.id',    description: 'Flow Identifier' },
      // { path: 'flow.trigger_type',   description: 'What triggered this flow' },
    ],
  },
  {
    id: 'customer_information',
    label: 'Customer Information',
    icon: FaUser,
    color: 'blue',
    variables: [
      { path: 'customer.id',           description: 'Customer unique ID' },
      { path: 'customer.first_name',   description: 'First name' },
      { path: 'customer.last_name',    description: 'Last name' },
      { path: 'customer.email',        description: 'Email address' },
      { path: 'customer.phone_number', description: 'Phone number' },
      { path: 'customer.account_status', description: 'Account status' },
      { path: 'customer.segment',      description: 'Customer segment' },
    ],
  },

];

// ─── Extract trigger variables from the startNode ─────────────────────────────
const extractTriggerVariables = (startNode) => {
  const contractFields = startNode?.data?.triggerContractFields || [];
  return contractFields.map((f) => ({
    path: `start.${f.jsonPath}`,
    description: `${f.type} · example: ${f.example ?? '—'}`,
    isDynamic: true,
  }));
};

// ─── Extract dynamic variables from an apiNode ────────────────────────────────
const extractApiNodeVariables = (node) => {
  const ns = node.data?.responseNamespace || node.data?.responseKey || 'api_response';
  const base = [
    { path: `api.${ns}.status_code`,     description: 'HTTP status code' },
    { path: `api.${ns}.response_body`,   description: 'Full response body' },
    { path: `api.${ns}.response_message`, description: 'Response message' },
    { path: `api.${ns}.error`,           description: 'Error (if any)' },
  ];

  // Append contract fields if user generated them from sample JSON
  const contractFields = node.data?.contractFields || [];

  const dynamic = contractFields.map((f) => ({
    path: `api.${ns}.${f.jsonPath.replace(/^\./, '')}`,
    description: `${f.type} · example: ${f.example ?? '—'}`,
    isDynamic: true,
  }));

  return [...base, ...dynamic];
};

// ─── Component ────────────────────────────────────────────────────────────────
const VariablePickerDrawer = ({ isOpen, onClose, onSelect, flowNodes = [] }) => {
  const [search, setSearch] = useState('');

  // Find all apiNode instances in the flow, deduplicated by namespace
  const allApiNodes = flowNodes.filter((n) => n.type === 'apiNode');
  const seenNs = new Set();
  const apiNodes = allApiNodes.filter((node) => {
    const ns = node.data?.responseNamespace || node.data?.responseKey || 'api_response';
    if (seenNs.has(ns)) return false;
    seenNs.add(ns);
    return true;
  });
  const startNode = flowNodes.find((n) => n.type === 'startNode');
  const triggerVars = extractTriggerVariables(startNode);

  const handleSelect = (varPath) => {
    onSelect(varPath);
    onClose();
  };

  const highlight = (text) => {
    if (!search) return text;
    const idx = text.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <Box as="mark" bg="yellow.200" color="inherit">{text.slice(idx, idx + search.length)}</Box>
        {text.slice(idx + search.length)}
      </>
    );
  };

  const filterVars = (vars) =>
    vars.filter(
      (v) =>
        !search ||
        v.path.toLowerCase().includes(search.toLowerCase()) ||
        (v.description || '').toLowerCase().includes(search.toLowerCase())
    );

  // ── Variable row ────────────────────────────────────────────────────────────
  const VarRow = ({ varObj, color }) => {
    const filtered = !search ||
      varObj.path.toLowerCase().includes(search.toLowerCase()) ||
      (varObj.description || '').toLowerCase().includes(search.toLowerCase());
    if (!filtered) return null;

    return (
      <ListItem>
        <Button
          size="xs"
          variant="ghost"
          width="full"
          justifyContent="flex-start"
          height="auto"
          py={1.5}
          px={2}
          _hover={{ bg: `#E2E8F0 !important` }}
          onClick={() => handleSelect(varObj.path)}
        >
          <VStack align="flex-start" spacing={0} w="full">
            <HStack spacing={1} w="full">
              <Code
                fontSize="11px"
                color={`${color}.700`}
                bg={`${color}.50`}
                px={1}
                borderRadius="sm"
                wordBreak="break-all"
                whiteSpace="normal"
                textAlign="left"
              >
                {`{{${varObj.path}}}`}
              </Code>
              {varObj.isDynamic && (
                <Tag fontSize="8px" colorScheme={color} variant="solid" flexShrink={0}>
                  dynamic
                </Tag>
              )}
            </HStack>
            {varObj.description && (
              <Text fontSize="10px" color="gray.500" textAlign="left" ml={1}>
                {varObj.description}
              </Text>
            )}
          </VStack>
        </Button>
      </ListItem>
    );
  };

  // ── Section header ──────────────────────────────────────────────────────────
  const SectionHeader = ({ meta, count }) => (
    <AccordionButton px={3} py={2} _hover={{ bg: `${meta.color}.50` }}>
      <HStack flex={1} spacing={2}>
        <Icon as={meta.icon} color={`${meta.color}.500`} boxSize={3} />
        <Text fontSize="xs" fontWeight="semibold">{meta.label}</Text>
        <Tag fontSize="9px" colorScheme={meta.color} variant="subtle">{count}</Tag>
      </HStack>
      <AccordionIcon />
    </AccordionButton>
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="sm">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="sm" borderBottomWidth="1px" py={3}>
          <HStack spacing={2}>
            <Icon as={FaCode} color="blue.500" boxSize={3.5} />
            <Text>Insert Flow Variable</Text>
          </HStack>
        </DrawerHeader>

        <DrawerBody p={3} overflowY="auto">
          {/* Search */}
          <InputGroup size="sm" mb={3}>
            <InputLeftElement pointerEvents="none">
              <Icon as={FaSearch} color="gray.400" boxSize={3} />
            </InputLeftElement>
            <Input
              placeholder="Search variables..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fontSize="xs"
            />
          </InputGroup>

          <Accordion
            allowMultiple
            defaultIndex={[0, 1, 2, 3, ...apiNodes.map((_, i) => i + 4)]}
          >
            {/* ── Static sections ─────────────────────────────────────────── */}
            {FLOW_CONTEXT_SECTIONS.map((section) => {
              const visible = filterVars(section.variables);
              if (search && visible.length === 0) return null;
              return (
                <AccordionItem
                  key={section.id}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  mb={2}
                  overflow="hidden"
                >
                  <SectionHeader
                    meta={section}
                    count={search ? visible.length : section.variables.length}
                  />
                  <AccordionPanel pb={2} px={1} bg="gray.50">
                    <List spacing={0.5}>
                      {section.variables.map((v) => (
                        <VarRow key={v.path} varObj={v} color={section.color} />
                      ))}
                    </List>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}

            {/* ── Trigger Payload variables ────────────────────────────────── */}
            {triggerVars.length > 0 && (() => {
              const visible = filterVars(triggerVars);
              if (search && visible.length === 0) return null;
              return (
                <AccordionItem
                  border="1px solid"
                  borderColor="green.200"
                  borderRadius="md"
                  mb={2}
                  overflow="hidden"
                >
                  <AccordionButton px={3} py={2} _hover={{ bg: 'green.50' }}>
                    <HStack flex={1} spacing={2}>
                      <Icon as={FaBolt} color="green.500" boxSize={3} />
                      <Text fontSize="xs" fontWeight="semibold">Trigger Payload</Text>
                      <Tag fontSize="9px" colorScheme="green" variant="subtle">
                        {search ? visible.length : triggerVars.length}
                      </Tag>
                    </HStack>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={2} px={1} bg="gray.50">
                    <List spacing={0.5}>
                      {triggerVars.map((v) => (
                        <VarRow key={v.path} varObj={v} color="green" />
                      ))}
                    </List>
                  </AccordionPanel>
                </AccordionItem>
              );
            })()}

            <Divider my={2} />
            <Text fontSize="10px" color="gray.400" px={1} mb={2} fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
              From Flow Nodes
            </Text>

            {/* ── Dynamic API nodes ────────────────────────────────────────── */}
            {apiNodes.length === 0 ? (
              <Center py={4} border="2px dashed" borderColor="gray.200" borderRadius="md">
                <HStack spacing={1} color="gray.400">
                  <Icon as={FaInfoCircle} boxSize={3} />
                  <Text fontSize="xs">No API nodes in this flow yet</Text>
                </HStack>
              </Center>
            ) : (
              apiNodes.map((node) => {
                const vars = extractApiNodeVariables(node);
                const visible = filterVars(vars);
                if (search && visible.length === 0) return null;
                const ns = node.data?.responseNamespace || node.data?.responseKey || 'api_response';

                return (
                  <AccordionItem
                    key={node.id}
                    border="1px solid"
                    borderColor="purple.200"
                    borderRadius="md"
                    mb={2}
                    overflow="hidden"
                  >
                    <AccordionButton px={3} py={2} _hover={{ bg: 'purple.50' }}>
                      <HStack flex={1} spacing={2}>
                        <Icon as={FaPlug} color="purple.500" boxSize={3} />
                        <Text fontSize="xs" fontWeight="semibold">
                          {node.data?.label || 'API Call'}
                        </Text>
                        <Tag fontSize="9px" colorScheme="purple" variant="subtle">
                          {ns}
                        </Tag>
                        {(node.data?.contractFields || []).length > 0 && (
                          <Tag fontSize="9px" colorScheme="green" variant="solid">
                            {node.data.contractFields.length} dynamic
                          </Tag>
                        )}
                      </HStack>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={2} px={1} bg="gray.50">
                      <List spacing={0.5}>
                        {vars.map((v) => (
                          <VarRow key={v.path} varObj={v} color="purple" />
                        ))}
                      </List>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })
            )}
          </Accordion>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default VariablePickerDrawer;